import { AssociationMember } from "@boark/shared/types/association-member";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Input } from "../../components/input";
import { useGetAssociation } from "../../queries/get-association";
import { inviteUser } from "../../services/auth";
import { useControlAssociationStore } from "../../stores/control-association";
import { useLocale } from "../../stores/locale";
import { getId } from "../../utils/id";

export function Users() {
  const { locale, currentLanguage } = useLocale();
  const queryClient = useQueryClient();
  const selectedAssociationId = useControlAssociationStore(
    (s) => s.selectedAssociationId,
  );
  if (!selectedAssociationId) {
    throw new Error("Selected association ID is required");
  }

  const associationId = getId(selectedAssociationId);

  const { data: association } = useGetAssociation(associationId);

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const members = useMemo(() => {
    return sortMembers(association?.members ?? []);
  }, [association?.members]);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <H1>{locale.auth.adminUsersTitle}</H1>
        <p className="text-sm text-gray-600">
          {locale.auth.adminUsersForAssociation}{" "}
          <span className="font-medium text-gray-800">{association?.name}</span>
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Link className="text-sm text-blue-700 hover:underline" to="/start">
            {locale.auth.controlStartChangeAssociation}
          </Link>
          <Link
            className="text-sm text-blue-700 hover:underline"
            to={`/association/${associationId}/edit`}
          >
            {locale.auth.associationEditLink}
          </Link>
        </div>
      </div>

      {members.length === 0 ? (
        <p className="text-sm text-gray-500">{locale.auth.membersEmpty}</p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-xs border border-gray-200">
          {members.map((member) => (
            <li
              key={member.id}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <span className="min-w-0 truncate text-sm">{member.email}</span>
              <span className="shrink-0 text-sm text-gray-600">
                {member.isOwner
                  ? locale.auth.memberTypeOwner
                  : locale.auth.memberTypeMember}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          (async () => {
            e.preventDefault();
            setIsSubmitting(true);
            setHasError(false);
            setIsSubmitted(false);
            try {
              await inviteUser(`association:${associationId}`, email, currentLanguage);
              setEmail("");
              setIsSubmitted(true);
              await queryClient.invalidateQueries({
                queryKey: ["associations", associationId],
              });
            } catch {
              setHasError(true);
            } finally {
              setIsSubmitting(false);
            }
          })();
        }}
      >
        <Input
          disabled={isSubmitting}
          hasError={hasError}
          placeholder={locale.login.email}
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="flex items-center justify-end gap-2">
          {hasError && (
            <p className="text-red-500">{locale.auth.inviteError}</p>
          )}
          {isSubmitted && !hasError && (
            <p className="text-green-700">{locale.auth.inviteSuccess}</p>
          )}
          <Button disabled={isSubmitting || email.length === 0} type="submit">
            {locale.auth.inviteSubmit}
          </Button>
        </div>
      </form>
    </div>
  );
}

function sortMembers(members: AssociationMember[]): AssociationMember[] {
  return [...members].sort((a, b) => {
    if (a.isOwner !== b.isOwner) {
      return a.isOwner ? -1 : 1;
    }
    return a.email.localeCompare(b.email);
  });
}
